import { useCallback } from 'react';
import type { Order } from '@hero/schema';
import { useAppWaybillBayanPdfApi } from '@hero/api-hooks';

/** Show a Bayan PDF in a pre-opened tab, or download it if the tab was blocked. */
const presentPdfFile = (
  file: { fileName: string; contentType: string; base64: string },
  tab: Window | null,
) => {
  const bytes = Uint8Array.from(atob(file.base64), char => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));

  if (tab && !tab.closed) {
    tab.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export interface UseGovernmentWaybillPdfArgs {
  /**
   * Archived PDF URL from a status read, when the caller already has one.
   *
   * Only an optimisation: the same URL is on the order document. Surfaces that
   * render one row per order (the orders table) must NOT fetch a status per row
   * just to fill this in.
   */
  pdfUrl?: string;
  /** Shown instead of the browser's own failure when the fetch fails. */
  onError?: () => void;
}

/**
 * Open an order's government waybill (وثيقة النقل) PDF.
 *
 * ## Why the stored URL is tried first
 *
 * Hero archives every issued document to Cloudinary, so there is normally a
 * plain URL to hand. Opening it directly is not just one request cheaper — it
 * keeps `window.open` inside the click handler, which browsers treat as a user
 * gesture and therefore don't block. The base64 path has to open a blank tab
 * up-front and fill it in when the response lands, and some browsers refuse
 * that. The fetch is the fallback for documents issued before Hero started
 * archiving, not the normal path.
 */
export const useGovernmentWaybillPdf = (
  order: Order,
  { pdfUrl, onError }: UseGovernmentWaybillPdfArgs = {},
) => {
  const fetchPdf = useAppWaybillBayanPdfApi();
  const storedUrl = pdfUrl ?? order.waybillBayan?.image?.url;

  const open = useCallback(() => {
    if (storedUrl) {
      window.open(storedUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (fetchPdf.isPending) return;

    const tab = window.open('about:blank', '_blank');
    fetchPdf.mutate(
      { orderId: String(order._id) },
      {
        onSuccess: file => presentPdfFile(file, tab),
        onError: () => {
          tab?.close();
          onError?.();
        },
      },
    );
  }, [storedUrl, fetchPdf, order._id, onError]);

  return {
    open,
    isPending: fetchPdf.isPending,
    /** True when opening costs no request — lets callers skip a spinner. */
    hasStoredUrl: !!storedUrl,
  };
};
